import { useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { useRouter } from 'next/router';

import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';
import useSearchPreferences from 'hooks/use-search-preferences';
import { useCategoryRouteParam } from 'src/shop/hooks/use-category-route-param';
import { useMarketplaceQueryParams } from 'shop/hooks/use-marketplace-query-params';
import { useClearYourCartModal } from 'src/components/modals/clear-your-cart/clear-your-cart-controller';
import { getPathToShopMarketplaceCategory } from 'src/utils/helpers/route';
import { validateAddressLimits } from 'shared/actions/address';

import ROUTES from 'src/routes';
import { OrderTypes } from 'types/graphql';
import { ORDER_TYPE_PICKUP } from 'shared/constants';
import { OnSaveLocationSettings, OnSaveLocationSettingsParams } from 'components/location-settings-popover';

export function useAttemptSaveLocationSettings(): OnSaveLocationSettings {
  const { push } = useRouter();
  const { category } = useCategoryRouteParam({ strict: false });
  const Cart = useCart();
  const apolloClient = useApolloClient();
  const { toggleLocationSettingsDropdown } = useUI();
  const saveLocationSettings = useSaveLocationSettings();
  const { open: openClearYourCartModal } = useClearYourCartModal();

  const attemptToSave = useCallback(
    async ({ address, orderType, distance }: OnSaveLocationSettingsParams): Promise<void> => {
      const save = (): void => {
        saveLocationSettings({ address, orderType, distance });
        toggleLocationSettingsDropdown();
      };

      if (orderType === ORDER_TYPE_PICKUP || Cart.isEmpty) {
        save();
        return;
      }

      const dispensaryInCartDeliversToAddress = await validateAddressLimits(Cart, apolloClient, address);
      if (dispensaryInCartDeliversToAddress) {
        save();
        return;
      }

      const { cartCleared } = await openClearYourCartModal();
      if (cartCleared) {
        save();
        void push(category ? getPathToShopMarketplaceCategory(category) : ROUTES.SHOP);
      }
    },
    [Cart, apolloClient, category, openClearYourCartModal, push, saveLocationSettings, toggleLocationSettingsDropdown]
  );

  return attemptToSave;
}

function useSaveLocationSettings(): OnSaveLocationSettings {
  const { updateAddress } = useCart();
  const { setOrderType } = useSearchPreferences();
  const { setQueryParams } = useMarketplaceQueryParams();

  const saveLocationSettings = useCallback(
    ({ address, distance, orderType }: OnSaveLocationSettingsParams): void => {
      updateAddress({ location: address });
      setOrderType(orderType);
      if (orderType === OrderTypes.pickup) {
        setQueryParams({ distance });
      }
    },
    [setOrderType, setQueryParams, updateAddress]
  );

  return saveLocationSettings;
}
